import React, { useCallback, useEffect, useRef, useState } from 'react';
import { bundleHashIn, cleanUrl, isNewer, refreshUrl, runningBundleHash } from './versionCheck';
import './UpdateBanner.css';

/** How often a page that stays open checks for a new version. */
export const CHECK_EVERY_MS = 5 * 60 * 1000;
/** Coming back to the tab checks at once, but not more than this often. */
const MIN_GAP_MS = 60 * 1000;

/**
 * The "new version" banner (2026-09-29; versionCheck.js has the why). A page
 * checks the live index.html every CHECK_EVERY_MS, and whenever the tab comes
 * back into view; when the site names a different bundle than the one
 * running, one bar says so with a **Refresh** button that loads the page
 * fresh. **Later** hides it until the NEXT version.
 *
 * Deliberately never automatic: a teacher mid-recap must not have the page
 * reload under her. Hard colours (the brand's deepest navy, white, teal), not
 * theme tokens, so it reads the same in every theme: white on #19284c is
 * 14.5:1, the button's navy on teal 7.5:1. Not on /stage, the room's
 * projector page (the room Mac is reloaded by hand).
 */
const UpdateBanner = ({ pathname = '' }) => {
	const [live, setLive] = useState(null);
	const [dismissed, setDismissed] = useState(null);
	const running = useRef(runningBundleHash());
	const lastCheck = useRef(0);

	const check = useCallback(async () => {
		if (!running.current || typeof fetch !== 'function') return;
		const now = Date.now();
		if (now - lastCheck.current < MIN_GAP_MS) return;
		lastCheck.current = now;
		try {
			const res = await fetch(`/index.html?check=${now}`, { cache: 'no-store' });
			if (!res.ok) return;
			const hash = bundleHashIn(await res.text());
			if (hash) setLive(hash);
		} catch (e) {
			/* offline or blocked: try again next time */
		}
	}, []);

	useEffect(() => {
		// Arrived through Refresh: take the marker back off the address.
		if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
			const clean = cleanUrl(window.location);
			if (clean) window.history.replaceState(window.history.state, '', clean);
		}
		if (!running.current) return undefined; // the dev server has no hashed bundle
		const t = setInterval(check, CHECK_EVERY_MS);
		const onVisible = () => document.visibilityState === 'visible' && check();
		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener('focus', onVisible);
		return () => {
			clearInterval(t);
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener('focus', onVisible);
		};
	}, [check]);

	if (pathname === '/stage' || !isNewer(running.current, live) || dismissed === live) return null;

	return (
		<div className="cyv-update" role="status" aria-live="polite">
			<i className="fa-sharp fa-solid fa-rotate" aria-hidden="true" />
			<p className="cyv-update__text">A new version of this page is available. Please click Refresh.</p>
			<button type="button" className="cyv-update__go" onClick={() => window.location.assign(refreshUrl(window.location, live))}>
				Refresh
			</button>
			<button type="button" className="cyv-update__later" onClick={() => setDismissed(live)}>
				Later
			</button>
		</div>
	);
};

export default UpdateBanner;
